/**
 * WatchManagementCard - Manage watch changes and handovers
 */

import React, { useState } from 'react';
import { Users, ChevronDown, ChevronUp, Plus } from 'lucide-react';

const WatchManagementCard = ({ 
  watchChanges,
  setWatchChanges,
  crew = [],
  isExpanded,
  onToggle,
  onAddLogEntry,
  activeVoyage
}) => {
  const [watchChange, setWatchChange] = useState({
    time: new Date().toTimeString().slice(0, 5),
    offWatchCrew: '',
    onWatchCrew: '',
    handoverNotes: ''
  });

  const handleRecordWatchChange = () => {
    if (!watchChange.offWatchCrew || !watchChange.onWatchCrew) {
      alert('Please select both off-watch and on-watch crew');
      return;
    }

    const change = {
      ...watchChange,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };

    setWatchChanges([...watchChanges, change]);

    // Create log entry
    const logEntry = {
      type: 'WATCH_CHANGE',
      category: 'crew',
      summary: `Watch change: ${watchChange.offWatchCrew} relieved by ${watchChange.onWatchCrew}`,
      details: change,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    };

    onAddLogEntry(logEntry);

    // Reset form
    setWatchChange({
      time: new Date().toTimeString().slice(0, 5),
      offWatchCrew: '',
      onWatchCrew: '',
      handoverNotes: ''
    });
  };

  return (
    <div className="rounded-lg border-2 bg-white border-gray-200">
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-gray-600" />
            <div>
              <h4 className="text-sm font-medium">Watch Management</h4>
              <p className="text-xs text-gray-500">
                {watchChanges.length} watch changes recorded
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {watchChanges.length > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                {watchChanges.length}
              </span>
            )}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Off Watch
                </label>
                <select
                  value={watchChange.offWatchCrew}
                  onChange={(e) => setWatchChange(prev => ({ ...prev, offWatchCrew: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select crew</option>
                  {crew.map(member => (
                    <option key={member.id} value={member.name}>
                      {member.name} - {member.role || member.position}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  On Watch
                </label>
                <select
                  value={watchChange.onWatchCrew}
                  onChange={(e) => setWatchChange(prev => ({ ...prev, onWatchCrew: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select crew</option>
                  {crew.map(member => (
                    <option key={member.id} value={member.name}>
                      {member.name} - {member.role || member.position}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Handover Notes
              </label>
              <textarea
                value={watchChange.handoverNotes}
                onChange={(e) => setWatchChange(prev => ({ ...prev, handoverNotes: e.target.value }))}
                placeholder="Any important information for the oncoming watch..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleRecordWatchChange}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Record Watch Change
            </button>

            {/* Recent Watch Changes */}
            {watchChanges.length > 0 && (
              <div className="border-t pt-3">
                <h5 className="text-xs font-medium text-gray-700 mb-2">Watch Schedule</h5>
                <div className="space-y-1">
                  {watchChanges.slice(-5).map((change) => (
                    <div key={change.id} className="text-xs bg-gray-50 p-2 rounded">
                      <span className="font-medium">{change.time}</span>
                      <span className="text-gray-600"> • {change.offWatchCrew} → {change.onWatchCrew}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchManagementCard;